// Import / export of a trip (a group of TaskPogoTrip legs) to a flat,
// human-readable JSON file. Mirrors LogbookIO.mjs.
//
// The trip's name is hoisted to the envelope and legs carry no `name` key:
// all legs of a group share one name (that IS the grouping contract in
// TripGroups.mjs), and hoisting keeps a hand-edited file from scattering
// legs across several groups. Import assigns the (deduped) name to every
// created leg.
//
// Only the `route` key of the reserved `fields` blob is exported -- routes go
// through routeFromTrip / mergeRouteIntoFields (TripRoute.mjs is the blob's
// single choke point), so a hand-edited file can't smuggle arbitrary blob
// keys in. Waypoint `id`s are session-internal (React keys / edit handles)
// and are stripped on export; routeFromTrip regenerates them on read.
//
// The file also carries a `places` section (PlaceIO.mjs): the TaskPogoPlace
// rows its routes refer to, so importing into another environment can recreate
// them. Without it a waypoint's `placeId` names a row that only exists where
// the file was written. The section is additive, so TRIP_EXPORT_VERSION stays 1.

import { routeFromTrip, mergeRouteIntoFields, resolveRouteAgainstPlaces } from './TripRoute.mjs';
import { placesForExport, placesForImport } from './PlaceIO.mjs';
import { inferLegDays } from './TripGroups.mjs';
import { tripIdentityDefaults } from './TripIdentity.mjs';

export const TRIP_EXPORT_FORMAT = 'taskpogo.trip';
export const TRIP_EXPORT_VERSION = 1;

// The editable leg columns that round-trip through an export file. `name` is
// hoisted to the envelope; everything else on a leg row (id, owner,
// timestamps, itemId, the raw fields blob) is server/context-bound and must
// not travel.
const LEG_EXPORT_KEYS = ['date', 'days', 'from', 'to', 'distance', 'notes'];

function exportedWaypoint(wp) {
  return {
    kind: wp.kind,
    ...(wp.name ? { name: wp.name } : {}),
    lat: wp.lat,
    lon: wp.lon,
    src: wp.src,
    ...(typeof wp.geocodedAt === 'string' && wp.geocodedAt ? { geocodedAt: wp.geocodedAt } : {}),
    ...(wp.autoName ? { autoName: true } : {}),
    // Unlike the waypoint `id` above -- session-internal, and stripped -- the
    // gazetteer binding is durable data worth carrying. On its own it is only
    // meaningful in the environment that wrote it; what makes it portable is
    // the `places` section, which carries the row itself so the importer can
    // recreate it and rebind by name (resolveRouteAgainstPlaces rule 3).
    ...(wp.placeId ? { placeId: wp.placeId } : {}),
    // A zero-day stopover and what it was. Durable data about the trip rather
    // than session state, and environment-independent -- unlike `placeId` it
    // names nothing outside the file -- so it simply travels. The import side
    // re-reads it through routeFromTrip, whose whitelist is what keeps a
    // hand-edited file from annotating an anchor.
    ...(wp.stopover ? { stopover: true } : {}),
    ...(wp.stopover && wp.stopNote ? { stopNote: wp.stopNote } : {}),
    ...(wp.stopover && wp.arriveAt ? { arriveAt: wp.arriveAt } : {}),
    ...(wp.stopover && wp.departAt ? { departAt: wp.departAt } : {}),
  };
}

// Build the export envelope for a trip's legs (the group page passes all of
// them, the leg page passes [trip]). The name comes from the legs' actual
// `name` column -- never a group's display name, which is synthetic for
// unnamed groups. `now` is injectable for deterministic tests.
export function serializeTrip(legs, { now = new Date(), placeIndex } = {}) {
  const name = (legs || [])
    .map((leg) => (typeof leg?.name === 'string' ? leg.name.trim() : ''))
    .find((n) => n !== '') || '';

  const routes = [];
  const exportedLegs = (legs || []).map((leg) => {
    const clean = {};
    for (const key of LEG_EXPORT_KEYS) {
      const value = leg?.[key];
      if (value === null || value === undefined) continue;
      clean[key] = value;
    }
    const route = routeFromTrip(leg);
    if (route) {
      clean.route = { v: route.v, waypoints: route.waypoints.map(exportedWaypoint) };
      routes.push(route);
    }
    return clean;
  });

  // Only when there is something to say, so a no-index export (or one taken
  // while the gazetteer was unreachable) is byte-identical to a pre-section
  // file. An export must never fail because the place list can't be read.
  const places = placesForExport(routes, placeIndex);

  return {
    format: TRIP_EXPORT_FORMAT,
    version: TRIP_EXPORT_VERSION,
    exportedAt: now.toISOString(),
    trip: { name },
    ...(places.length > 0 ? { places } : {}),
    legs: exportedLegs,
  };
}

function coerceNumber(leg, key, index) {
  const value = leg[key];
  if (value === null || value === undefined || value === '') return undefined;
  const num = Number(value);
  if (typeof value === 'boolean' || !Number.isFinite(num)) {
    throw new Error(`Leg ${index + 1} has an invalid ${key}.`);
  }
  return num;
}

// Parse + validate raw file text into a trusted { trip: { name }, legs }
// shape. Throws an Error with a human-readable message on any problem so
// callers can route it straight to showError. Legs come back with their
// route already normalized (fresh waypoint ids, [from, ...vias, to] order).
export function parseTripExport(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('This file is not valid JSON.');
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('This file is not a TaskPogo trip export.');
  }
  if (data.format !== TRIP_EXPORT_FORMAT) {
    throw new Error('This file is not a TaskPogo trip export.');
  }
  if (data.version !== TRIP_EXPORT_VERSION) {
    throw new Error(`Unsupported trip export version: ${data.version}.`);
  }

  const trip = data.trip;
  if (!trip || typeof trip !== 'object' || Array.isArray(trip)) {
    throw new Error('This trip export is missing its trip details.');
  }

  if (!Array.isArray(data.legs)) {
    throw new Error('This trip export has no legs list.');
  }
  if (data.legs.length === 0) {
    throw new Error('This trip export has no legs.');
  }

  const legs = data.legs.map((leg, index) => {
    if (!leg || typeof leg !== 'object' || Array.isArray(leg)) {
      throw new Error(`Leg ${index + 1} is not a valid object.`);
    }
    if (leg.date === null || leg.date === undefined || leg.date === '') {
      throw new Error(`Leg ${index + 1} is missing a date.`);
    }
    // The schema's `date` is an AWSDateTime, which AppSync only accepts as a
    // full ISO string -- so require a string and normalize it here (a
    // hand-edited "2026-06-01" becomes "2026-06-01T00:00:00.000Z") rather
    // than let a raw GraphQL error surface mid-import.
    if (typeof leg.date !== 'string' || Number.isNaN(new Date(leg.date).getTime())) {
      throw new Error(`Leg ${index + 1} has an invalid date.`);
    }

    const clean = { date: new Date(leg.date).toISOString() };
    if (typeof leg.from === 'string' && leg.from !== '') clean.from = leg.from;
    if (typeof leg.to === 'string' && leg.to !== '') clean.to = leg.to;
    if (typeof leg.notes === 'string' && leg.notes !== '') clean.notes = leg.notes;
    const distance = coerceNumber(leg, 'distance', index);
    if (distance !== undefined) clean.distance = distance;
    // A leg occupies whole days, and the column is an AppSync Int, so a
    // fraction or a zero has to be rejected here rather than mid-import.
    const days = coerceNumber(leg, 'days', index);
    if (days !== undefined) {
      if (!Number.isInteger(days) || days < 1) {
        throw new Error(`Leg ${index + 1} has an invalid days.`);
      }
      clean.days = days;
    }
    // A legacy file's `hours` key just isn't read: this whitelist is the whole
    // contract, and rejecting it would make every file exported before `days`
    // existed un-importable.

    if (leg.route !== null && leg.route !== undefined) {
      // Reuse the app's tolerant route reader for normalization, but an
      // unreadable route in an import file is a real error, not "no route".
      const route = routeFromTrip({ fields: { route: leg.route } });
      if (!route) {
        throw new Error(`Leg ${index + 1} has an invalid route.`);
      }
      clean.route = route;
    }
    return clean;
  });

  // A file written before `days` existed (or hand-edited without it) carries a
  // schedule only in its dates. Read the stays back off those gaps so the
  // imported trip is chain-consistent from the start -- otherwise every leg
  // would land on `days` 1 and the first reorder would compress the whole trip
  // onto consecutive days. Dates themselves are taken as authoritative: the
  // file is a complete schedule, so import never cascades.
  //
  // inferLegDays measures each leg against the NEXT one, so it needs date
  // order -- which the file is under no obligation to be in. The app's own
  // exports are ordered, but a hand-edited one need not be, and inferring
  // straight down an unordered list would measure stays against whichever leg
  // happened to be typed next (and go negative-then-clamped-to-1 wherever the
  // file steps backwards). Infer on a sorted view and write the answers back
  // to the legs they belong to; `legs` itself keeps the file's order.
  const byDate = legs
    .map((leg, index) => ({ leg, index }))
    .sort((a, b) => new Date(a.leg.date).getTime() - new Date(b.leg.date).getTime());
  const inferred = inferLegDays(byDate.map((entry) => entry.leg));
  byDate.forEach((entry, position) => {
    if (entry.leg.days === undefined) entry.leg.days = inferred[position];
  });

  // The place rows to ensure exist before the legs are created: the file's own
  // section, plus a minimal pin for any name its routes refer to that the
  // section doesn't cover. A missing or malformed section is NOT a parse error
  // -- the legs are the user's data and the places are a bonus.
  const places = placesForImport(data.places, legs.map((leg) => leg.route).filter(Boolean));

  return {
    trip: { name: typeof trip.name === 'string' ? trip.name : '' },
    places,
    legs,
  };
}

// Turn parsed legs into create-mutation inputs for a target item. The legs
// are already whitelisted by parseTripExport; the route lands back in the
// `fields` blob as a plain object (Database.prepareTripInput stringifies it
// for AWSJSON). Pass name = null to create unnamed legs.
//
// `placeIndex` is the TARGET environment's gazetteer, after the importer has
// created whatever was missing. Binding here means the stored blob names rows
// that exist rather than the source environment's ids. Without one this is
// exactly what it always was: resolveRouteAgainstPlaces returns the same route
// for an absent or empty index.
export function legsToCreateInputs(legs, { itemId, name, tripGroupId, placeIndex }) {
  // The identity every created leg shares, so an imported multi-leg file is one
  // trip by the same rule everything else is -- and so importing the same file
  // twice yields two trips rather than one with the legs interleaved.
  // tripIdentityDefaults keeps the "never an id without a name" invariant, which
  // is what makes an unnamed single-leg import still stand alone.
  const identity = tripIdentityDefaults({ name, tripGroupId });
  return (legs || []).map((leg) => {
    const { route: parsed, ...columns } = leg;
    const route = resolveRouteAgainstPlaces(parsed, placeIndex);
    return {
      ...columns,
      ...identity,
      ...(route ? { fields: mergeRouteIntoFields({}, route) } : {}),
      itemId,
    };
  });
}

// The update-mutation payload that replaces `existingTrip`'s editable
// columns and route with a parsed leg's. Deliberately never touches `name`
// OR `tripGroupId` -- the leg keeps its trip; a replace changes where the leg
// goes, not which trip it is part of -- and merges the route into the trip's
// latest `fields` blob so any other keys in it survive. Columns the file
// omits are cleared -- replace means the leg becomes what the file says.
export function legToUpdateInput(leg, existingTrip) {
  // An empty merged blob must go out as an explicit null: prepareTripInput
  // DROPS an empty `fields` object from the mutation, and AppSync leaves an
  // omitted attribute untouched -- the old route would silently survive.
  const merged = mergeRouteIntoFields(existingTrip.fields, leg.route ?? null);
  return {
    id: existingTrip.id,
    itemId: existingTrip.itemId,
    date: leg.date,
    days: leg.days ?? null,
    from: leg.from ?? null,
    to: leg.to ?? null,
    distance: leg.distance ?? null,
    notes: leg.notes ?? null,
    fields: Object.keys(merged).length > 0 ? merged : null,
  };
}

// Pick a trip name that doesn't collide with the item's existing trip
// groups. Case-INSENSITIVE: group membership lowercases names
// (TripGroups.tripGroupKey), so "desolation 2026" and "Desolation 2026"
// are the same trip. "X" -> "X (imported)" -> "X (imported 2)" -> ...
export function uniqueTripName(desired, existingNames) {
  const taken = new Set((existingNames || []).map((n) => String(n).trim().toLowerCase()));
  const base = (desired && desired.trim()) || 'Imported Trip';
  if (!taken.has(base.toLowerCase())) return base;
  const first = `${base} (imported)`;
  if (!taken.has(first.toLowerCase())) return first;
  let n = 2;
  while (taken.has(`${base} (imported ${n})`.toLowerCase())) n += 1;
  return `${base} (imported ${n})`;
}

// A friendly download filename, e.g. "desolation-2026-2026-08-06.json".
export function suggestedTripFileName(name, now = new Date()) {
  const slug =
    (name || 'trip')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'trip';
  const date = now.toISOString().slice(0, 10);
  return `${slug}-${date}.json`;
}
